import { GridColDef } from '@mui/x-data-grid-pro'
import { useTranslation } from 'react-i18next'
import { BillListItemFragment, BillListProductionItemFragment, BillsListQuery, ProductFragment } from 'src/api/api.generated'
import { useHotelDate, useTenant } from 'src/app/hooks/context/context.hook'
import { PosPasserbyBillsListRow } from './pos-passerby-bills-types'
import { v4 as uuid } from 'uuid'
import useFormatCurrency from 'src/app/hooks/current-locale/use-format-currency'
import useFormatDate from 'src/app/hooks/current-locale/use-format-date'
import { TextField, Tooltip } from '@mui/material'
import { Stack } from '@vapor/material'
import { MobileDatePicker } from '@mui/x-date-pickers-pro'
import { Typography } from '@vapor/material'
import { LocalizedString, useConfirm } from 'hic-components'
import useLocalizeString from 'src/app/hooks/current-locale/use-localized-string'
import { isDateBetween, compareDates } from 'src/utils/date'
import { DeleteAction } from 'src/app/components/data-grid-action-cell-item'

export default function usePosPasserbyBillsList(onDeleteBillItemClick: (rowId: string, canRemove: boolean) => void, updateRow: (updatedRow: PosPasserbyBillsListRow) => void) {
  const { t } = useTranslation()
  const { currency } = useTenant()
  const { formatTableCurrency } = useFormatCurrency()
  const formatDate = useFormatDate()
  const hotelDate = useHotelDate()
  const confirm = useConfirm()
  const localizeString = useLocalizeString()

  const columns: GridColDef[] = [
    {
      field: 'competenceDate',
      headerName: t('pms.booking.pos.passerby-bills-list.competence_date'),
      flex: 1,
      maxWidth: 120,
      minWidth: 120,
      sortable: false,
      valueFormatter: ({ value }) => {
        return value ? formatDate(value) : value
      },
      renderCell: (params) => {
        const row = params.row as PosPasserbyBillsListRow
        return 'productionId' in row ? (
          <MobileDatePicker //
            disabled={!row.editable}
            value={row.competenceDate || null}
            minDate={row.checkin ?? undefined}
            maxDate={row.checkout ?? undefined}
            onChange={(date) => {
              if (date !== null) {
                const newRow = { ...row }
                newRow.competenceDate = date
                updateRow(newRow)
              }
            }}
            slotProps={{
              textField: {
                variant: 'standard',
              },
            }}
          />
        ) : (
          <b>{t('pms.booking.pos.passerby-bills-list.total')}</b>
        )
      },
    },
    {
      field: 'quantity',
      headerName: t('pms.booking.pos.passerby-bills-list.quantity'),
      flex: 1,
      maxWidth: 80,
      minWidth: 70,
      sortable: false,
      renderCell: (params) => {
        const row = params.row as PosPasserbyBillsListRow
        return 'productionId' in row ? (
          <TextField
            disabled={!row.editable}
            variant="standard"
            value={row.quantity}
            type="number"
            onChange={(event) => {
              let tmp = parseInt(event.target.value)
              if (isNaN(tmp) || tmp < 1) {
                tmp = 1
              }
              if (tmp) {
                tmp = Math.round(tmp)
              }
              const newRow = { ...row }
              newRow.quantity = tmp
              newRow.total = newRow.amount * tmp
              updateRow(newRow)
            }}
          />
        ) : (
          <></>
        )
      },
    },
    {
      field: 'detail',
      headerName: t('pms.booking.pos.passerby-bills-list.detail'),
      flex: 1,
      minWidth: 180,
      sortable: false,
      renderCell: (params) => {
        const row = params.row as PosPasserbyBillsListRow
        return (
          <Tooltip title={row.detail}>
            <Stack spacing={0}>
              <Typography
                variant="body"
                style={{
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  minHeight: '1rem',
                }}
              >
                {row.detail}
              </Typography>
              {row.reservedResourceId && <Typography variant="caption">{row.reservedResourceOrigin}</Typography>}
            </Stack>
          </Tooltip>
        )
      },
    },
    {
      field: 'category',
      headerName: t('pms.booking.pos.passerby-bills-list.category'),
      flex: 1,
      minWidth: 150,
      sortable: false,
    },
    {
      field: 'vat',
      headerName: t('pms.booking.pos.passerby-bills-list.vat'),
      flex: 1,
      maxWidth: 80,
      minWidth: 70,
      sortable: false,
      renderCell: (params) => {
        const row = params.row as PosPasserbyBillsListRow
        return 'productionId' in row ? row.vat : <></>
      },
    },
    {
      field: 'amount',
      headerName: t('pms.booking.pos.passerby-bills-list.amout'),
      flex: 1,
      type: 'number',
      maxWidth: 120,
      minWidth: 100,
      sortable: false,
      valueFormatter: ({ value }) => {
        return value ? formatTableCurrency(value, currency) : value
      },
    },
    {
      field: 'total',
      headerName: t('pms.booking.pos.passerby-bills-list.total'),
      flex: 1,
      type: 'number',
      maxWidth: 120,
      minWidth: 100,
      sortable: false,
      valueFormatter: ({ value }) => {
        return value ? formatTableCurrency(value, currency) : value
      },
    },
    {
      field: 'actions',
      type: 'actions',
      minWidth: 60,
      sortable: false,
      headerName: t('pms.booking.pos.passerby-bills-list.actions'),
      getActions: (params) => {
        const row = params.row as PosPasserbyBillsListRow
        return 'productionId' in row
          ? [
              <DeleteAction //
                onClick={() => {
                  if (row.productionId) {
                    confirm({
                      title: t('pms.booking.pos.delete_production_confirm_title'),
                      message: row.reservedResourceId ? t('pms.booking.pos.delete_rr_production_confirm_message') : t('pms.booking.pos.delete_production_confirm_message'),
                      confirmIsDanger: true,
                      confirmButtonText: t('commons.confirm'),
                      onConfirm: () => onDeleteBillItemClick(row.id, true),
                      cancelButtonText: t('commons.cancel'),
                    })
                  } else {
                    onDeleteBillItemClick(row.id, row.editable)
                  }
                }}
              />,
            ]
          : []
      },
    },
  ]

  const billsQueryAsRows = (queryResponse: BillsListQuery | BillListItemFragment | null): PosPasserbyBillsListRow[] => {
    if (queryResponse) {
      const { productions, isPasserByBill } =
        isPasserbyBillsListQuery(queryResponse) && queryResponse && (queryResponse.bills ?? []).length === 1
          ? { productions: queryResponse?.bills?.[0].productions ?? [], isPasserByBill: queryResponse?.bills?.[0].reservedResourceId ? false : true }
          : isPasserbyListItemFragment(queryResponse)
          ? { productions: queryResponse.productions, isPasserByBill: queryResponse.reservedResourceId ? false : true }
          : { productions: [], isPasserByBill: false }

      return (
        productions
          ?.filter((production) => (isPasserByBill ? true : production.production.manual === true))
          ?.map((production) => {
            const { production: prod, quota } = production
            const amount = quota * prod.amount
            const total = quota * prod.total

            return {
              id: uuid(),
              competenceDate: prod.date,
              quantity: prod.quantity,
              productionId: prod.id,
              detail: prod.description || (!prod.reservedResourceId ? t('pms.booking.pos.passerby-bills-list.missing_production_description') : ''),
              currency: prod.currency,
              amount: amount,
              total,
              chargeCategoryId: prod.chargeCategoryId,
              category: prod.chargeCategory.name,
              vat: prod.vat.code ?? '',
              vatId: prod.vatId,
              editable: prod.amount === amount && (!isPasserByBill || !prod.reservedResourceId), // Editable if it's not a result of a split
              reservedResourceId: prod.reservedResourceId ?? undefined,
              reservedResourceOrigin: getProductionDescription(prod, localizeString),
              checkin: getCheckinFromBill(
                isPasserbyBillsListQuery(queryResponse) && queryResponse && (queryResponse.bills ?? []).length === 1
                  ? queryResponse.bills?.[0] ?? null
                  : isPasserbyListItemFragment(queryResponse)
                  ? queryResponse
                  : null,
              ),
              checkout: getCheckoutFromBill(
                isPasserbyBillsListQuery(queryResponse) && queryResponse && (queryResponse.bills ?? []).length === 1
                  ? queryResponse.bills?.[0] ?? null
                  : isPasserbyListItemFragment(queryResponse)
                  ? queryResponse
                  : null,
              ),
            }
          }) ?? []
      )
    }
    return []
  }

  const productFragmentAsRow = (productFragments: ProductFragment, bill: BillToCompare | { id: string } | null): PosPasserbyBillsListRow => {
    const checkin = bill && 'reservedResource' in bill ? getCheckinFromBill(bill) : null
    const checkout = bill && 'reservedResource' in bill ? getCheckoutFromBill(bill) : null

    const competenceDate = getCompetenceDate(hotelDate, checkin, checkout)

    return {
      id: uuid(),
      competenceDate,
      quantity: 1,
      productionId: '',
      productId: productFragments.id,
      detail: productFragments.description,
      currency: currency,
      amount: productFragments.amount,
      total: productFragments.amount,
      chargeCategoryId: productFragments.chargeCategoryId,
      category: productFragments.chargeCategory.name,
      vat: productFragments.chargeCategory.defaultVat.code,
      vatId: productFragments.chargeCategory.defaultVatId,
      editable: true,
      checkin,
      checkout,
    }
  }

  return { billsQueryAsRows, productFragmentAsRow, columns }
}

function isPasserbyBillsListQuery(value: BillsListQuery | BillListItemFragment): value is BillsListQuery {
  return Object.prototype.hasOwnProperty.call(value, 'bills')
}

function isPasserbyListItemFragment(value: BillsListQuery | BillListItemFragment): value is BillListItemFragment {
  return Object.prototype.hasOwnProperty.call(value, 'id')
}

/**
 * Composes the description using first guest name and resource if the production is related to a reserved resource
 * @param production
 * @param localizeString
 * @returns
 */
function getProductionDescription(production: BillListProductionItemFragment, localizeString: (localizedString: LocalizedString | null | undefined) => string): string {
  let result = ''
  if (production.reservedResource) {
    const resource = production.reservedResource.resourceOccupancies?.[production.reservedResource.resourceOccupancies?.length - 1]?.resource
    const { reservationCode } = production.reservedResource.reservation
    let resourceType: string | undefined = undefined
    let resourceName: string | undefined = undefined

    const guestName = production.reservedResource.guestOccupancies?.find((g) => isDateBetween(production.date, g.start, g.end))?.guest.name
    if (resource) {
      resourceType = resource.resourceType?.code || localizeString(resource.resourceType?.customName) || localizeString(resource.resourceType.name.description)
      resourceName = resource?.code
    }

    if (reservationCode) result += `${reservationCode}`
    if (guestName) result += ` ${guestName}`
    if (resourceType) result += ` ${resourceType}`
    if (resourceName) result += ` ${resourceName}`
  }

  return result
}

type ReservedResourceToCompare = { start?: Date; end?: Date }
export type BillToCompare = { reservedResource?: ReservedResourceToCompare[] | null }

export function getCheckinFromBill(bill?: BillToCompare | null) {
  return (
    bill?.reservedResource?.reduce((a: ReservedResourceToCompare | null, b: ReservedResourceToCompare | null) => (a?.start && b?.start ? (a.start < b.start ? a : b) : a ? a : b ? b : null), null)
      ?.start ?? null
  )
}

export function getCheckoutFromBill(bill?: BillToCompare | null) {
  return (
    bill?.reservedResource?.reduce((a: ReservedResourceToCompare | null, b: ReservedResourceToCompare | null) => (a?.end && b?.end ? (a.end > b.end ? a : b) : a ? a : b ? b : null), null)?.end ?? null
  )
}

export function getCompetenceDate(hotelDate: Date, checkin: Date | null, checkout: Date | null) {
  let competenceDate = hotelDate
  if (checkin && checkout) {
    if (compareDates(hotelDate, checkin) < 0) competenceDate = checkin
    if (compareDates(checkout, hotelDate) < 0) competenceDate = checkout
  }

  return competenceDate
}
